import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { motion } from 'motion/react';
import { scrimIn } from './motion';
import { WizardStepContext, type WizardModalName, type WizardStepValue } from './wizardStepContext';

/**
 * OverlayFrame — the reskin's persistent modal frame (approach A).
 *
 * The Design app's `UploadWizard` owns the persistent bordered box, the scrim, and
 * the `WizardModalName` context that `ModalShell` reports its accessible name up
 * to. But `UploadWizard` is a ROUTER shell — it derives the step from the URL,
 * portals to body, and runs its own inert-siblings focus trap. The backend does
 * none of that: `useUploadOverlay` owns the step state, the focus (`vm.dialogRef`
 * + `vm.onKeyDown` handle Escape + Tab-cycle), and the close gesture.
 *
 * So this is a THIN frame that keeps every visible pixel of the Design's box and
 * scrim while replacing only the router/portal/trap plumbing with the backend's:
 *   - The box classes are copied VERBATIM from UploadWizard's persistent frame
 *     (`relative flex w-full max-w-modal flex-col` / `h-modal max-h-full` /
 *     `rounded-lg border border-ink bg-surface`), so the box is pixel-identical.
 *   - The centring box + scrim are the Design's (`top-header`, `p-modal-x`,
 *     `bg-scrim`, `scrimIn`), so the overlay looks the same.
 *   - The `role="dialog"` box wires the BACKEND focus trap: `ref={vm.dialogRef}` +
 *     `onKeyDown={vm.onKeyDown}` + `tabIndex={-1}`, and focuses itself once on
 *     mount (the ref callback, matching the current reskin). No inert sweep here —
 *     the hook's `onKeyDown` cycles Tab within the dialog, which is the backend's
 *     established behaviour; going global on inert is a separate, later decision.
 *   - The backdrop dismiss is `onMouseDown` → `onClose` (NOT onClick), matching
 *     the current reskin: a text-selection drag that ends on the backdrop must not
 *     close the modal.
 *   - `WizardModalName` context is provided here so the ported `ModalShell` works
 *     UNCHANGED — it reports `{labelledBy, label}` up and this frame renders the
 *     resulting `aria-labelledby`/`aria-label` on the dialog box.
 *
 * The root still carries `.tb-ov-reskin`, but it NO LONGER SCOPES ANYTHING. The reset
 * and tokens are global now, loaded once from src/design-system.css at app root, so no
 * stylesheet targets this class — it is kept only as a stable DOM/test hook for "this
 * subtree is the V2 overlay". Do not reintroduce scoped styling on it.
 *
 * The frame renders INLINE where the overlay mounts (no portal), so despite `fixed` it
 * remains a DOM descendant of whatever page mounted it — on /share that is a
 * `.tb-tools` section whose V1 element rules still match these buttons. See the long
 * note in tools/shared.tsx.
 */

/**
 * `WizardModalName` / `WizardStepContext` / `useWizardModalName` USED TO BE DECLARED
 * HERE. They now live in `./wizardStepContext` because the copied Design frame
 * (`../pages/UploadWizard`) also provides this context, and two `createContext`
 * calls would give the two frames separate channels — leaving whichever frame lost
 * the coin toss with a permanently unnamed dialog. See that module's header. This
 * frame still PROVIDES the context, exactly as before; only the declaration moved.
 */

export interface OverlayFrameProps {
  /** The backend focus-trap target. The hook focuses it on open + each stage. */
  dialogRef: MutableRefObject<HTMLDivElement | null>;
  /** The hook's keydown handler: Escape → close, Tab → cycle within the dialog. */
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Backdrop dismiss (matches Cancel/close). */
  onClose: () => void;
  /** The fading contents — a ModalShell wrapped in the caller's AnimatePresence. */
  children: ReactNode;
}

export function OverlayFrame({ dialogRef, onKeyDown, onClose, children }: OverlayFrameProps) {
  // The current step's accessible name, reported up by its ModalShell and rendered
  // on this persistent frame. State so the frame re-renders its aria-* on a step
  // change.
  const [modalName, setModalName] = useState<WizardModalName>({});
  const stepValue = useMemo<WizardStepValue>(() => ({ setModalName }), [setModalName]);

  // Backdrop dismiss on mousedown — fires only when the press LANDS ON the backdrop
  // element itself (not bubbled from inside the box), so a click inside never
  // closes. onMouseDown (not onClick) so a text-drag ending on the backdrop is not
  // treated as a dismiss.
  const onBackdropMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    // The overlay root. `.tb-ov-reskin` is an inert marker class (no CSS targets it) —
    // the reset + tokens are global; see the header.
    // `fixed inset-0` is viewport-pinned overlay behaviour, not a canvas coordinate.
    <div className="tb-ov-reskin fixed inset-0 z-50">
      {/* The scrim covers the full viewport including the header. Fades in on open
          (scrimIn). It is also a backdrop dismiss target. */}
      <motion.div
        variants={scrimIn}
        initial="hidden"
        animate="visible"
        exit="exit"
        onMouseDown={onBackdropMouseDown}
        className="absolute inset-0 bg-scrim"
      />

      {/* The centring box: starts BELOW the header (top-header), centres the modal,
          and dismisses on a backdrop press in the free space around the box. */}
      <div
        onMouseDown={onBackdropMouseDown}
        className="absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x"
      >
        {/* THE PERSISTENT FRAME — the measured 1056x640 rounded, stroked box, box
            classes copied VERBATIM from the Design's UploadWizard. It is the
            `role="dialog"` and the backend focus-trap target. Focuses itself once
            on mount via the ref callback (matching the current reskin), while also
            handing the node to the hook's dialogRef. */}
        <div
          ref={(el) => {
            dialogRef.current = el;
            if (el && !el.dataset.focused) {
              el.dataset.focused = 'true';
              el.focus();
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalName.labelledBy}
          aria-label={modalName.labelledBy ? undefined : modalName.label}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className={[
            'relative flex w-full max-w-modal flex-col',
            'h-modal max-h-full',
            'rounded-lg border border-ink bg-surface',
          ].join(' ')}
        >
          <WizardStepContext.Provider value={stepValue}>{children}</WizardStepContext.Provider>
        </div>
      </div>
    </div>
  );
}
